/**
 * TTS (Text-to-Speech) utility functions
 */

/**
 * Calculate the duration of TTS audio based on text content
 * @param {string} text - The text to be converted to speech
 * @param {number} wpm - Words per minute (default: 160)
 * @returns {number} Duration in seconds
 */
export const tts_duration_seconds = (text, wpm = 160) => {
  if (!text || typeof text !== 'string') {
    return 0;
  }

  // Count words using regex
  const words = text.match(/\b[\w']+\b/g);
  const wordCount = words ? words.length : 0;
  
  // Base duration calculation
  const base = (60.0 * wordCount) / Math.max(wpm, 1);

  // Calculate pauses
  let pause = 0;
  
  // Comma pauses
  pause += 0.2 * (text.match(/,/g) || []).length;
  
  // Semicolon and colon pauses
  pause += 0.3 * ((text.match(/;/g) || []).length + (text.match(/:/g) || []).length);
  
  // Sentence ending pauses
  pause += 0.5 * ((text.match(/\./g) || []).length + 
                  (text.match(/\?/g) || []).length + 
                  (text.match(/!/g) || []).length);
  
  // Paragraph breaks
  pause += 1.0 * (text.match(/\n\n/g) || []).length;

  // Extra considerations
  
  // Numbers (rough estimate)
  const numbers = text.match(/\b\d[\d,./-]*\b/g) || [];
  pause += 0.2 * numbers.length;
  
  // Acronyms (e.g., "U.S.A.", "Ph.D.")
  const acronyms = text.match(/\b(?:[A-Za-z]\.){2,}/g) || [];
  const acronymLetters = acronyms.reduce((total, acronym) => {
    return total + acronym.replace(/\./g, '').length;
  }, 0);
  pause += 0.1 * acronymLetters;
  
  // Long words (12+ characters)
  const longWords = words ? words.filter(word => word.length >= 12).length : 0;
  pause += 0.07 * longWords;

  return Math.max(0.1, base + pause); // Minimum 0.1 seconds
};

/**
 * Extract variables from TTS text (e.g., {{bride_name}}, {{venue}})
 * @param {string} text - The text containing variables
 * @returns {Array<string>} Array of variable names (without braces)
 */
export const extractTTSVariables = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables = [];
  let match;

  while ((match = variableRegex.exec(text)) !== null) {
    const variableName = match[1].trim();
    if (variableName && !variables.includes(variableName)) {
      variables.push(variableName);
    }
  }

  return variables;
};

/**
 * Replace variables in TTS text with actual values
 * @param {string} text - The text containing variables
 * @param {Object} variables - Object with variable names as keys and values as values
 * @returns {string} Text with variables replaced
 */
export const replaceTTSVariables = (text, variables = {}) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  let result = text;
  
  // Replace each variable
  Object.keys(variables).forEach(key => {
    const variablePattern = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    result = result.replace(variablePattern, variables[key] || `{{${key}}}`);
  });

  return result;
};

/**
 * Validate TTS text and return any issues
 * @param {string} text - The TTS text to validate
 * @returns {Object} Validation result with isValid and issues array
 */
export const validateTTSText = (text) => {
  const issues = [];
  
  if (!text || typeof text !== 'string') {
    issues.push('Text is required');
    return { isValid: false, issues };
  }

  if (text.trim().length === 0) {
    issues.push('Text cannot be empty');
    return { isValid: false, issues };
  }

  if (text.length > 5000) {
    issues.push('Text is too long (maximum 5000 characters)');
  }

  // Check for unclosed variables
  const openBraces = (text.match(/\{\{/g) || []).length;
  const closeBraces = (text.match(/\}\}/g) || []).length;
  
  if (openBraces !== closeBraces) {
    issues.push('Unclosed variable brackets detected');
  }

  // Check for empty variables
  const emptyVariables = text.match(/\{\{\s*\}\}/g);
  if (emptyVariables) {
    issues.push('Empty variable brackets found');
  }

  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Format TTS text for display (truncate if too long)
 * @param {string} text - The TTS text
 * @param {number} maxLength - Maximum length to display (default: 50)
 * @returns {string} Formatted text
 */
export const formatTTSTextForDisplay = (text, maxLength = 50) => {
  if (!text || typeof text !== 'string') {
    return 'Empty TTS';
  }

  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return trimmed.substring(0, maxLength - 3) + '...';
};
